import { fromCents, summarizeMaturityAllocations, toCents } from "./allocation";
import { getClassificationAncestors, getClassificationPath } from "./hierarchy";
import { buildDebtServiceSchedule, } from "pages/DebtStudio/lib/buildDebtServiceSchedule";
import {
  buildIssueCouponScheduleFromDeal,
  mapDealToScheduleForm,
} from "pages/DebtStudio/lib/buildPortfolioDebtService";

export const PROJECT_DIMENSION = "project";
export const ISSUE_DIMENSION = "issue";
export const PAYMENT_DATE_DIMENSION = "paymentDate";

export function expandReportingDimensions(dimensions, classifications) {
  return (dimensions || []).flatMap((dimension) => {
    const classification = (classifications || []).find((item) => item.id === dimension);
    if (!classification) return [{ key: dimension, sourceDimension: dimension, label: dimension }];
    const maxDepth = Math.max(1, ...(classification.values || []).map(
      (value) => getClassificationAncestors(classification.values, value.id).length + 1
    ));
    if (maxDepth === 1) {
      return [{ key: dimension, sourceDimension: dimension, label: classification.name, classificationLevel: 0 }];
    }
    return Array.from({ length: maxDepth }, (_, level) => ({
      key: `${dimension}:level:${level + 1}`,
      sourceDimension: dimension,
      label: `${classification.name} Level ${level + 1}`,
      classificationLevel: level,
    }));
  });
}

function dimensionValue(record, descriptor, context) {
  const dimension = descriptor.sourceDimension;
  if (dimension === PROJECT_DIMENSION) {
    const project = context.projects.find((item) => item.id === record.projectId);
    return {
      key: project?.id || "unclassified",
      label: project?.project_name || "Unclassified",
    };
  }
  if (dimension === ISSUE_DIMENSION) return { key: record.issueId, label: record.issueName || "Unknown Issue" };
  if (dimension === PAYMENT_DATE_DIMENSION) return { key: record.paymentDate, label: record.paymentDate };

  const classification = context.classifications.find(
    (item) => item.id === dimension
  );
  const valueId = record.classifications[dimension];
  if (classification && descriptor.classificationLevel != null && !valueId) {
    return descriptor.classificationLevel === 0
      ? { key: "unclassified", label: "Unclassified" }
      : null;
  }
  if (classification && descriptor.classificationLevel != null && valueId) {
    const value = classification.values.find((item) => item.id === valueId);
    if (!value) return { key: "unclassified", label: "Unclassified" };
    const path = [...getClassificationAncestors(classification.values, valueId), value];
    const levelValue = path[descriptor.classificationLevel];
    return levelValue ? { key: levelValue.id, label: levelValue.name } : null;
  }
  return {
    key: valueId || "unclassified",
    label:
      (classification && valueId
        ? getClassificationPath(classification.values, valueId)
        : "") || "Unclassified",
  };
}

export function getReportingDimensionValue(record, dimension, context = {}, classificationLevel = null) {
  return dimensionValue(record, {
    sourceDimension: dimension,
    classificationLevel,
  }, {
    projects: context.projects || [],
    classifications: context.classifications || [],
  });
}

export function buildAllocationRecords({ workspace }) {
  const componentById = new Map(
    (workspace?.components || []).map((component) => [component.id, component])
  );
  const classificationsByComponent = new Map();
  (workspace?.assignments || []).forEach((assignment) => {
    const values = classificationsByComponent.get(assignment.component_id) || {};
    if (!values[assignment.classification_id]) {
      values[assignment.classification_id] = assignment.classification_value_id;
    }
    classificationsByComponent.set(assignment.component_id, values);
  });

  return (workspace?.amounts || [])
    .filter((amount) => Number(amount.allocated_amount || 0) !== 0)
    .map((amount) => {
      const component = componentById.get(amount.component_id);
      return {
        componentId: amount.component_id,
        projectId: component?.project_id || null,
        maturityDate: amount.maturity_date,
        allocatedCents: toCents(amount.allocated_amount),
        classifications: classificationsByComponent.get(amount.component_id) || {},
      };
    });
}

export function buildOutstandingBalanceRecords({ issues = [], workspace, asOfDate, asOfDates = [] }) {
  const issueById = new Map(issues.map((issue) => [issue.id, issue]));
  const componentById = new Map(
    (workspace?.components || []).map((component) => [component.id, component])
  );
  const classificationsByComponent = new Map();
  (workspace?.assignments || []).forEach((assignment) => {
    const values = classificationsByComponent.get(assignment.component_id) || {};
    if (!values[assignment.classification_id]) {
      values[assignment.classification_id] = assignment.classification_value_id;
    }
    classificationsByComponent.set(assignment.component_id, values);
  });

  const snapshotDates = [...new Set(asOfDates.length ? asOfDates : [asOfDate])]
    .filter(Boolean)
    .sort();
  return snapshotDates.flatMap((snapshotDate) =>
    (workspace?.amounts || []).flatMap((amount) => {
      const maturityDate = String(amount.maturity_date || "").slice(0, 10);
      const outstandingBalanceCents = maturityDate > snapshotDate
        ? toCents(amount.allocated_amount)
        : 0;
      if (!outstandingBalanceCents) return [];
      const component = componentById.get(amount.component_id);
      const issue = issueById.get(component?.ds_deal_id);
      return [{
        issueId: component?.ds_deal_id || "",
        issueName: issue?.name || "Unknown Issue",
        componentId: amount.component_id,
        projectId: component?.project_id || null,
        paymentDate: snapshotDate,
        balanceAsOfDate: snapshotDate,
        allocatedCents: 0,
        interestCents: 0,
        outstandingBalanceCents,
        classifications: classificationsByComponent.get(amount.component_id) || {},
      }];
    })
  );
}

function outstandingBalanceCentsAtLatestDate(records) {
  const latestDate = records.reduce(
    (latest, record) => record.balanceAsOfDate > latest ? record.balanceAsOfDate : latest,
    ""
  );
  return records.reduce((sum, record) =>
    record.balanceAsOfDate === latestDate
      ? sum + (record.outstandingBalanceCents || 0)
      : sum, 0);
}

export function buildAllocationHierarchy({
  records,
  dimensions,
  projects,
  classifications,
  portfolioDebtServiceCents,
}) {
  if (!dimensions.length) return [];
  const context = { projects, classifications };
  const expandedDimensions = expandReportingDimensions(dimensions, classifications);

  function buildGroups(groupRecords, level, path, groupValues, depth) {
    if (level >= expandedDimensions.length) return [];
    const groups = new Map();
    const missingRecords = [];
    groupRecords.forEach((record) => {
      const value = dimensionValue(record, expandedDimensions[level], context);
      if (!value) {
        missingRecords.push(record);
        return;
      }
      const group = groups.get(value.key) || { ...value, records: [] };
      group.records.push(record);
      groups.set(value.key, group);
    });

    const groupedNodes = [...groups.values()]
      .sort((left, right) => left.label.localeCompare(right.label))
      .map((group) => {
        const allocatedCents = group.records.reduce(
          (sum, record) => sum + record.allocatedCents,
          0
        );
        const interestCents = group.records.reduce((sum, record) => sum + (record.interestCents || 0), 0);
        const outstandingBalanceCents = outstandingBalanceCentsAtLatestDate(group.records);
        const nextPath = [...path, group.key];
        const children = buildGroups(
          group.records,
          level + 1,
          nextPath,
          [...groupValues, group.label],
          depth + 1
        );
        return {
          id: nextPath.join("/"), key: nextPath.join("/"),
          depth,
          label: group.label,
          grouping: `${"› ".repeat(depth)}${group.label}`,
          groupValues: [...groupValues, group.label],
          allocatedPrincipal: fromCents(allocatedCents),
          allocatedInterest: fromCents(interestCents),
          allocatedDebtService: fromCents(allocatedCents + interestCents),
          outstandingBalance: fromCents(outstandingBalanceCents),
          percentOfIssue:
            portfolioDebtServiceCents > 0
              ? ((allocatedCents + interestCents) / portfolioDebtServiceCents) * 100
              : null,
          isSubtotal: children.length > 0,
          is_expandable: children.length > 0,
          children,
        };
      });
    return [
      ...groupedNodes,
      ...buildGroups(
        missingRecords,
        level + 1,
        path,
        [...groupValues, ""],
        depth
      ),
    ];
  }

  return buildGroups(records, 0, [], [], 0);
}

const allocationMetrics = (records) => {
  const principalCents = records.reduce((sum, record) => sum + (record.allocatedCents || 0), 0);
  const interestCents = records.reduce((sum, record) => sum + (record.interestCents || 0), 0);
  const outstandingBalanceCents = outstandingBalanceCentsAtLatestDate(records);
  return {
    allocatedPrincipal: fromCents(principalCents),
    allocatedInterest: fromCents(interestCents),
    allocatedDebtService: fromCents(principalCents + interestCents),
    outstandingBalance: fromCents(outstandingBalanceCents),
  };
};

function pivotColumnValue(record, dimension, context) {
  if (!dimension) return { key: "all", label: "Value" };
  if (dimension === PAYMENT_DATE_DIMENSION) {
    return { key: record.paymentDate || "undated", label: record.paymentDate || "Undated" };
  }
  if (dimension === PROJECT_DIMENSION) {
    const project = context.projects.find((item) => item.id === record.projectId);
    return { key: project?.id || "unclassified", label: project?.project_name || "Unclassified" };
  }
  if (dimension === ISSUE_DIMENSION) {
    return { key: record.issueId || "unknown", label: record.issueName || "Unknown Issue" };
  }
  const classification = context.classifications.find((item) => item.id === dimension);
  const valueId = record.classifications?.[dimension];
  return {
    key: valueId || "unclassified",
    label: (classification && valueId
      ? getClassificationPath(classification.values, valueId)
      : "") || "Unclassified",
  };
}

/** Builds the row hierarchy and column buckets for the configurable matrix report. */
export function buildAllocationPivot({ records = [], rowDimensions = [], columnDimension = "", projects = [], classifications = [] }) {
  const context = { projects, classifications };
  const rowDescriptors = expandReportingDimensions(rowDimensions, classifications);
  const columnMap = new Map();
  records.forEach((record) => {
    const value = pivotColumnValue(record, columnDimension, context);
    if (!columnMap.has(value.key)) columnMap.set(value.key, value);
  });
  const columnGroups = [...columnMap.values()].sort((left, right) => left.label.localeCompare(right.label));

  const decorate = (node, nodeRecords) => {
    const cells = {};
    columnGroups.forEach((column) => {
      cells[column.key] = allocationMetrics(nodeRecords.filter(
        (record) => pivotColumnValue(record, columnDimension, context).key === column.key
      ));
    });
    return { ...node, ...allocationMetrics(nodeRecords), cells };
  };

  function buildGroups(groupRecords, level, path, groupValues, depth) {
    if (level >= rowDescriptors.length) return [];
    const groups = new Map();
    const missingRecords = [];
    groupRecords.forEach((record) => {
      const value = dimensionValue(record, rowDescriptors[level], context);
      if (!value) { missingRecords.push(record); return; }
      const group = groups.get(value.key) || { ...value, records: [] };
      group.records.push(record);
      groups.set(value.key, group);
    });
    const nodes = [...groups.values()].sort((a, b) => a.label.localeCompare(b.label)).map((group) => {
      const nextPath = [...path, group.key];
      const children = buildGroups(group.records, level + 1, nextPath, [...groupValues, group.label], depth + 1);
      return decorate({
        id: nextPath.join("/"), key: nextPath.join("/"), depth, label: group.label,
        groupValues: [...groupValues, group.label],
        isSubtotal: children.length > 0, is_expandable: children.length > 0, children,
      }, group.records);
    });
    return [...nodes, ...buildGroups(missingRecords, level + 1, path, [...groupValues, ""], depth)];
  }

  const hierarchy = rowDescriptors.length
    ? buildGroups(records, 0, [], [], 0)
    : [decorate({ id: "total", key: "total", depth: 0, label: "Total", isSubtotal: true, is_expandable: false, children: [] }, records)];
  return { hierarchy, columnGroups };
}

export function flattenAllocationHierarchy(nodes, expandedKeys) {
  const rows = [];
  const visit = (node) => {
    rows.push(node);
    if (!node.children?.length || !expandedKeys.has(node.key)) return;
    node.children.forEach(visit);
  };
  (nodes || []).forEach(visit);
  return rows;
}

export function getAllocationCopyRows(nodes) {
  const rows = [];
  const visit = (node) => {
    if (!node.children?.length) {
      const groupingColumns = Object.fromEntries(
        (node.groupValues || []).map((value, index) => [`group_${index}`, value])
      );
      rows.push({ ...node, ...groupingColumns });
      return;
    }
    node.children.forEach(visit);
  };
  (nodes || []).forEach(visit);
  return rows;
}

export function buildGroupedAllocationRows(options) {
  const hierarchy = buildAllocationHierarchy(options);
  const expandedKeys = new Set();
  const visit = (nodes) => (nodes || []).forEach((node) => {
    if (node.is_expandable) expandedKeys.add(node.key);
    visit(node.children);
  });
  visit(hierarchy);
  return flattenAllocationHierarchy(hierarchy, expandedKeys);
}

function parseRows(structure) {
  if (Array.isArray(structure)) return structure;
  try { const parsed = JSON.parse(structure || "[]"); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
}

export function distributeCentsByLargestRemainder(items, targetCents, valueKey) {
  if (!items.length || targetCents === 0) return new Map(items.map((item) => [item.key, 0]));
  const totalWeight = items.reduce((sum, item) => sum + Math.max(0, Number(item[valueKey] || 0)), 0);
  if (totalWeight <= 0) return new Map(items.map((item) => [item.key, 0]));
  const ranked = items.map((item) => {
    const exact = (Math.max(0, Number(item[valueKey] || 0)) / totalWeight) * targetCents;
    const cents = Math.floor(exact);
    return { key: item.key, cents, remainder: exact - cents };
  });
  let remaining = targetCents - ranked.reduce((sum, item) => sum + item.cents, 0);
  ranked.sort((left, right) => right.remainder - left.remainder || left.key.localeCompare(right.key));
  for (let index = 0; index < remaining; index += 1) ranked[index % ranked.length].cents += 1;
  return new Map(ranked.map((item) => [item.key, item.cents]));
}

export function buildPortfolioAllocationDebtServiceRecords({ issues, workspace, startDate = "", endDate = "" }) {
  const assignments = new Map();
  (workspace.assignments || []).forEach((item) => {
    const current = assignments.get(item.component_id) || {};
    if (!current[item.classification_id]) current[item.classification_id] = item.classification_value_id;
    assignments.set(item.component_id, current);
  });
  const componentsByIssue = new Map();
  (workspace.components || []).forEach((component) => {
    const list = componentsByIssue.get(component.ds_deal_id) || [];
    list.push(component); componentsByIssue.set(component.ds_deal_id, list);
  });
  const amountMap = new Map((workspace.amounts || []).map((item) => [`${item.component_id}:${item.maturity_date}`, Number(item.allocated_amount || 0)]));
  const records = [];

  (issues || []).forEach((issue) => {
    const components = componentsByIssue.get(issue.id) || [];
    if (!components.length) return;
    const form = mapDealToScheduleForm(issue);
    const structureRows = parseRows(issue.structure);
    const sourceByPayment = new Map(buildIssueCouponScheduleFromDeal(issue).map((payment) => [String(payment.date || "").slice(0, 10), payment]));
    const principalByMaturity = new Map();
    const baseByPayment = new Map();
    const candidates = new Map();
    structureRows.forEach((row) => {
      const date = String(row.date || row.maturity_date || "").slice(0, 10);
      principalByMaturity.set(date, (principalByMaturity.get(date) || 0) + Number(row.principal || 0));
    });
    structureRows.forEach((row) => {
      const maturityDate = String(row.date || row.maturity_date || "").slice(0, 10);
      const maturityPrincipal = principalByMaturity.get(maturityDate) || 0;
      if (!maturityDate || maturityPrincipal <= 0 || !form) return;
      const schedule = buildDebtServiceSchedule({ rows: [row], form });
      schedule.forEach((payment) => {
        const paymentDate = String(payment.date || "").slice(0, 10);
        const base = baseByPayment.get(paymentDate) || { principal: 0, interest: 0 };
        base.principal += Number(payment.principal || 0);
        base.interest += Number(payment.interest || 0);
        baseByPayment.set(paymentDate, base);
      });
      components.forEach((component) => {
        const allocatedAtMaturity = amountMap.get(`${component.id}:${maturityDate}`) || 0;
        if (!allocatedAtMaturity) return;
        const share = allocatedAtMaturity / maturityPrincipal;
        schedule.forEach((payment) => {
          const paymentDate = String(payment.date || "").slice(0, 10);
          const key = `${paymentDate}:${component.id}`;
          const candidate = candidates.get(key) || { key, component, paymentDate, principal: 0, interest: 0 };
          candidate.principal += Number(payment.principal || 0) * share;
          candidate.interest += Number(payment.interest || 0) * share;
          candidates.set(key, candidate);
        });
      });
    });
    const candidatesByPayment = new Map();
    candidates.forEach((candidate) => {
      const list = candidatesByPayment.get(candidate.paymentDate) || [];
      list.push(candidate); candidatesByPayment.set(candidate.paymentDate, list);
    });
    candidatesByPayment.forEach((paymentCandidates, paymentDate) => {
      if ((startDate && paymentDate < startDate) || (endDate && paymentDate > endDate)) return;
      const source = sourceByPayment.get(paymentDate) || {};
      const base = baseByPayment.get(paymentDate) || {};
      const rawPrincipal = paymentCandidates.reduce((sum, item) => sum + item.principal, 0);
      const rawInterest = paymentCandidates.reduce((sum, item) => sum + item.interest, 0);
      const principalTarget = base.principal > 0 ? toCents(Number(source.principal || 0) * rawPrincipal / base.principal) : 0;
      const interestTarget = base.interest > 0 ? toCents(Number(source.interest || 0) * rawInterest / base.interest) : 0;
      const principalByCandidate = distributeCentsByLargestRemainder(paymentCandidates, principalTarget, "principal");
      const interestByCandidate = distributeCentsByLargestRemainder(paymentCandidates, interestTarget, "interest");
      paymentCandidates.forEach((candidate) => {
        const principalCents = principalByCandidate.get(candidate.key) || 0;
        const interestCents = interestByCandidate.get(candidate.key) || 0;
        if (!principalCents && !interestCents) return;
        records.push({ issueId: issue.id, issueName: issue.name, componentId: candidate.component.id, projectId: candidate.component.project_id, paymentDate, allocatedCents: principalCents, interestCents, classifications: assignments.get(candidate.component.id) || {} });
      });
    });
  });
  return records;
}

export function buildMaturityReportRows(issue, workspace) {
  return summarizeMaturityAllocations(issue?.maturities || [], workspace?.amounts || []).map(
    (row) => ({
      id: row.maturityDate,
      maturityDate: row.maturityDate,
      actualPrincipal: fromCents(row.principalCents),
      allocatedPrincipal: fromCents(row.allocatedCents),
      variance: fromCents(row.varianceCents),
      status: row.status,
    })
  );
}
